import { Injectable } from '@angular/core';
import { DataService, NotificationService } from '@vendure/admin-ui/core';
import { firstValueFrom, map, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import {
  EXPORT_MUTATION,
  IMPORT_PRICING_RULES_MUTATION,
  type ExportPricingRulesResult,
  type ImportPricingRulesResult,
} from './pricing-rule-admin-api';

@Injectable({ providedIn: 'root' })
export class PricingRuleDataService {
  constructor(
    private dataService: DataService,
    private notificationService: NotificationService,
  ) {}

  async importPricingRules(file: File) {
    return firstValueFrom(
      this.dataService
        .mutate<ImportPricingRulesResult>(IMPORT_PRICING_RULES_MUTATION, {
          file,
        })
        .pipe(map((result) => result.importPricingRules)),
    );
  }

  exportPricingRulesToCsv() {
    // Show a warning notification about excluded coupon-only promotions
    this.notificationService.warning(
      'Note: Coupon-only promotions (without SKUs or PDGs) are excluded from the export.',
      { duration: 8000 },
    );

    return this.exportPricingRules().subscribe({
      error: () => {
        this.notificationService.error('marketing.export-failed');
      },
    });
  }

  private exportPricingRules() {
    return this.dataService
      .mutate<ExportPricingRulesResult>(EXPORT_MUTATION)
      .pipe(
        switchMap((result) => {
          if (result?.exportPricingRules) {
            const blob = new Blob([result.exportPricingRules.csvContent], {
              type: 'text/csv',
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'pricing-rules.csv';
            a.click();
            window.URL.revokeObjectURL(url);
            this.notificationService.success('marketing.export-success');
          }
          return of(undefined);
        }),
      );
  }
}
